import { Controller } from "@hotwired/stimulus";
import { Autosave } from "../lib/autosave";
import { DocumentDraft } from "../lib/document_draft";
import { t } from "../lib/i18n";
import { mediaKind, publishPlacements } from "../lib/toolbox_placement";
import {
  audioCandidates,
  audioSettings,
  bindSegmentSlot,
  isMediumMissing,
  trackReference,
  unbindSegmentSlot,
} from "../lib/composition_v3";
import {
  ensureSegmentIds,
  ensureDimensions,
  DEFAULT_WIDTH,
  DEFAULT_HEIGHT,
  totalOutputDuration,
  ensureDurationCoversOverlays,
  resolveSnaps,
  formatTime,
  hasPlaceholderSegment,
  segmentOutputDuration,
  segmentOutputStart,
  segmentId,
  setAllTransitions,
} from "../lib/composition_v3";
import { preset, recalcAnimations } from "../lib/animation_presets.ts";
import { MOTION_KINDS, motionAnimation, motionOf } from "../lib/segment_motion.ts";
import { TRANSITION_KINDS } from "../lib/segment_transition.ts";
import {
  gsap,
  D,
  animateIn,
  animateOut,
} from "../lib/editor_animations";
import { TooltipManager } from "../lib/editor_tooltips";
import { RemotionPreview } from "../lib/remotion_preview";
import { EditorPlayback } from "../lib/editor_playback";
import { UndoManager } from "../lib/undo_manager";
import { MediaPicker } from "../lib/media_picker";
import { heroicon } from "../lib/heroicon";
import playSvg from "heroicons/20/solid/play.svg?raw";
import pauseSvg from "heroicons/20/solid/pause.svg?raw";

// Un plan resté vide part chez Remotion comme un aplat de couleur : la publication sort avec un
// trou à la place de la vidéo. On bloque l'enregistrement plutôt que de laisser passer.
const PLACEHOLDER_BLOCKS_SAVE = "Ajoutez une vidéo au plan vide avant d'enregistrer";

export default class extends Controller {
  static outlets = [
    "composition-editor--fabric",
    "composition-editor--tracks",
  ];

  static targets = [
    "editor",
    "retryForm",
    "error",
    "info",
    "preview",
    "previewArea",
    "artboard",
    "scrubTc",
    "timeCur",
    "timeTotal",
    "btnPlay",
    "btnUndo",
    "btnRedo",
    "btnSave",
    "btnSaveWrapper",
    "tooltip",
    "tooltipInner",
    "toasts",
    "jsonDrawer",
    "saveState",
    // Templates
    "toastTpl",
  ];

  static values = {
    previewUrl: { type: String, default: "http://localhost:5000/preview" },
    compositionJson: String,
    flash: { type: String, default: "" },
    libraryDialog: String,
    unsavedMessage: String,
    panel: { type: Boolean, default: true },
    documentUrl: { type: String, default: "" },
    // Le contrat des brouillons (plan document-drafts) : le commit (Valider, Enregistrer,
    // Publier selon l'hôte) et l'abandon (le destroy du brouillon, que Quitter appelle).
    commitUrl: { type: String, default: "" },
    discardUrl: { type: String, default: "" },
  };

  get libraryDialog() {
    return this.libraryDialogValue && document.getElementById(this.libraryDialogValue);
  }

  // ==================== Public API (for sub-controllers via outlet) ====================

  /** The current composition object (mutable reference). */
  get composition() { return this._composition; }

  // ==================== Les gestes d'une séquence (sa barre de page) ====================

  setSegmentSpeed(index, speed) { this._tracks?.setSegmentSpeed(index, speed); }
  toggleSegmentRepeat(index) { this._tracks?.toggleSegmentRepeat(index); }
  duplicateSegment(index) { this._tracks?.duplicateSegment(index); }
  deleteSegment(index) { this._tracks?.deleteSegment(index); }

  /** Relancer le plan IA échoué de la séquence (Documents::SequencesController#retry) : un POST
   *  Turbo dont la réponse diffuse le document replanifié. */
  retrySegment(index) {
    const seg = this._composition.segments?.[index];
    if (!seg?.id || !this.hasRetryFormTarget) return;
    const form = this.retryFormTarget;
    form.action = form.dataset.urlTemplate.replace("SEGMENT_ID", encodeURIComponent(seg.id));
    form.requestSubmit();
  }

  /** The current selection state: { kind, index } or null. */
  get selection() { return this._sel; }

  /** Current playhead time in seconds. */
  get playTime() { return this._playback?.time ?? 0; }

  /** Update playhead time (called by tracks controller). */
  setPlayTime(t) {
    this._playback?.setTime(t);
    this._fabric?.refreshPage();
  }

  /** Seek preview to a given time and update playhead (called by tracks controller). */
  goTo(t) {
    this._playback?.goTo(t);
  }

  /** Seek preview to an exact frame (called by tracks controller for audio resync). */
  seekTo(frame) {
    this._preview.seekTo(frame);
  }

  /** Notify a mutation from a sub-controller. Triggers resolveSnaps, undo push, re-render. */
  mutate() { this._afterMutation(); }

  /** Clear selection (used by sub-controllers to close themselves). */
  deselect() { this._select(null); }

  /** Select a specific item (used by fabric / tracks controllers). */
  select(kind, index) { this._select(kind, index); }

  /** Annonce la cible courante des médias (en-tête de la toolbox, gel des uploads). */
  publishPlacement() { publishPlacements(this._placements()); }

  /** Open the media picker with a given intent (e.g. "fabric:add", "fabric:replace"). */
  openPicker(intent, ctx) { this._picker?.open(intent, ctx); }

  /** Sync segment muted state based on whether a soundtrack is present. */
  syncSegmentAudio() { this._syncSegmentAudio(); }

  /** Replace a segment's media (used by tracks drop). */
  replaceSegmentMedia(cm, ctx) { return this._replaceSegmentMedia(cm, ctx); }

  /** Insert a new segment from media (used by tracks drop). */
  insertSegmentFromMedia(cm, ctx) { return this._insertSegmentFromMedia(cm, ctx); }

  /** Insert a placeholder sequence and select it (used by the tracks "+"). */
  insertPlaceholderSegment(index) { this._insertPlaceholderSegment(index); }

  /** Select a sequence and open one page of its background in the toolbox (`video`,
   *  `image`, `color`) — used by the tracks to fill or replace a sequence's media. */
  openSegmentBackground(index, panel) {
    this._select("segment", index);
    this._fabric?.showSegmentPage(index, panel);
  }

  /** Access to the fabric sub-controller (used by tracks for syncVisibility). */
  get fabric() { return this._fabric; }

  /** Access to the tooltip manager (used by tracks for trim drag feedback). */
  get tooltip() { return this._tooltip; }

  /** Show scrub timecode pill over the preview (called by tracks controller). */
  showScrubTc() {
    this.scrubTcTarget.textContent = formatTime(this.playTime);
    gsap.to(this.scrubTcTarget, {
      autoAlpha: 1,
      duration: D(0.12),
      overwrite: "auto",
    });
    clearTimeout(this._scrubTimer);
    this._scrubTimer = setTimeout(
      () => gsap.to(this.scrubTcTarget, { autoAlpha: 0, duration: D(0.3) }),
      600,
    );
  }

  /** Update mobile timecode displays (called by tracks controller after setPlayhead). */
  updateTimecodes(curTxt, totalTxt) {
    this.timeCurTargets.forEach((el) => { el.textContent = curTxt; });
    this.timeTotalTargets.forEach((el) => { el.textContent = totalTxt; });
  }

  /**
   * Called by the fabric controller after objects are moved/scaled/rotated.
   * Recalculates animation values and triggers dirty + undo + render.
   */
  /** Un calque a bougé : les animations relatives à sa taille se recalculent, puis le
   *  rituel commun à toute mutation. */
  onFabricModified() {
    for (const ov of this._composition.overlays || []) {
      if (ov._fabricId) recalcAnimations(ov);
    }
    this._afterMutation();
  }

  /** Set overlay group expanded state. */
  setGroupOpen(open) {
    this._tracks?.setGroupOpen(open);
  }

  /** Show a toast message. */
  toast(msg, withUndo = false) { this._toast(msg, withUndo); }

  // ==================== Template bag ====================

  get _tpl() {
    return {
      clone: (name) => this[`${name}Target`].content.cloneNode(true).firstElementChild,
    };
  }

  // ==================== Lifecycle ====================

  connect() {
    // Toolbox — listen for item selections from the left panel
    this._boundOnToolboxItem = this._onToolboxItemSelected.bind(this);
    this.element.addEventListener("toolbox-item:selected", this._boundOnToolboxItem);

    // Zoom — listen for zoom changes from the editor-zoom controller
    this._boundOnZoom = (e) => this._applyZoom(e.detail?.zoom);
    this.element.addEventListener("editor-zoom:change", this._boundOnZoom);

    // Overlay animation/snap events from the animations form
    this._onSnapToggle = (e) => this._handleSnapToggle(e.detail);
    this._onAddAnim = (e) => this._handleAddAnim(e.detail);
    this._onRemoveAnim = (e) => this._handleRemoveAnim(e.detail);
    this._onAnimDuration = (e) => this._handleAnimDuration(e.detail);
    this._onAnimSide = (e) => this._handleAnimSide(e.detail);
    this._onAnimOption = (e) => this._handleAnimOption(e.detail);
    this._onSegmentColor = (e) => this._handleSegmentColor(e.detail);
    this._onSegmentMotion = (e) => this._handleSegmentMotion(e.detail);
    this._onSegmentMotionAmplitude = (e) => this._handleSegmentMotionAmplitude(e.detail);
    window.addEventListener("overlay:snap-toggle", this._onSnapToggle);
    window.addEventListener("overlay:add-anim", this._onAddAnim);
    window.addEventListener("overlay:remove-anim", this._onRemoveAnim);
    window.addEventListener("overlay:set-anim-duration", this._onAnimDuration);
    window.addEventListener("overlay:set-anim-side", this._onAnimSide);
    window.addEventListener("overlay:set-anim-option", this._onAnimOption);
    window.addEventListener("background:set-color", this._onSegmentColor);
    window.addEventListener("background:set-motion", this._onSegmentMotion);
    window.addEventListener("background:set-motion-amplitude", this._onSegmentMotionAmplitude);

    this._composition = null;
    this._sel = null;

    this._jsonDrawerOpen = false;

    try {
      const raw = JSON.parse(this.compositionJsonValue || "{}");
      this._composition = ensureDimensions(ensureSegmentIds(raw));
      resolveSnaps(this._composition);
    } catch (e) {
      this._composition = {
        version: 3,
        fps: 30,
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
        segments: [],
        overlays: [],
        audio: null,
      };
    }

    // Modules
    this._tooltip = new TooltipManager(this.tooltipTarget, this.tooltipInnerTarget);
    this._preview = new RemotionPreview(this.previewTarget, {
      onFrameUpdate: (frame) => this._playback.frameUpdate(frame),
      onReady: () => this._sendToPreview(),
      onRangeEnd: () => this._playback.rangeEnded(),
    });
    this._playback = new EditorPlayback({
      player: this._preview,
      fps: () => this._composition.fps || 30,
      sync: () => this._sendToPreview(),
      syncAndWait: () => this._sendToPreviewAndWait(),
      showCanvas: (visible, opts) => this._fabric?.setEditable(visible, opts),
      // La page sous la tête suit au repos ; pendant la lecture, la timeline seule.
      onTime: (t) => {
        this._tracks?.setPlayhead(t);
        if (!this._playback.rendersBlocks) this._fabric?.refreshPage();
      },
      onPlayingChange: (playing) => {
        this.btnPlayTarget.replaceChildren(
          heroicon(playing ? pauseSvg : playSvg, playing ? "w-3.5 h-3.5" : "w-3.5 h-3.5 ml-0.5"),
        );
      },
      saveSelection: () => { this._selBeforePlay = this._sel ? { ...this._sel } : null; },
      restoreSelection: () => this.restoreSelection(),
    });

    // Global listeners
    this._mouseOverHandler = (e) => this._tooltip.onMouseOver(e.target);
    this._pointerDownHandler = () => this._tooltip.onPointerDown();
    document.addEventListener("mouseover", this._mouseOverHandler);
    document.addEventListener("pointerdown", this._pointerDownHandler);

    // Media library — routes selections to the right handler
    if (this.libraryDialog) {
      this._picker = new MediaPicker(this.libraryDialog);
      this._picker.on("fabric:add", (cm) => this._fabric?.addImage(cm));
      this._picker.on("fabric:replace", (cm) => this._fabric?.replaceImage(cm));
    }

    // Undo/redo manager (snapshots the full composition JSON, including fabric overlays)
    this._undo = new UndoManager({
      serialize: () => JSON.stringify(this._composition),
      restore: async (snapshot) => {
        this._composition = JSON.parse(snapshot);
        this._sel = null;
        await this._fabric?.restoreOverlays();
      },
    });
    this._undo.onStateChange = (state) => {
      this.btnUndoTarget.disabled = !state.canUndo;
      this.btnRedoTarget.disabled = !state.canRedo;
    };
    this._undo.initialize();

    // Le document a-t-il changé depuis l'ouverture ? (la confirmation de Quitter)
    this._dirty = false;

    this._updateJsonEditor();
    this._tracks?.renderAll();
    this._refreshSaveButton();

    // Autosave (éditeur pleine page monté sur une compo persistée) : debounce + pastille.
    if (this.documentUrlValue) {
      this._autosave = new Autosave({
        url: this.documentUrlValue,
        getPayload: () => JSON.stringify(this._composition),
        onState: (state) => this._setSaveState(state),
        onSaved: () => this._fabric?.reloadOverlayPanel(),
      });
      this._autosave.prime();
    }

    // Le brouillon (plan document-drafts) : commit et Quitter, communs aux deux éditeurs. En
    // lecteur readonly (PROVISOIRE), sans contrat : Quitter ferme simplement le panel.
    this._draft = new DocumentDraft({
      element: this.element,
      commitUrl: this.commitUrlValue,
      discardUrl: this.discardUrlValue,
      autosave: this._autosave,
      isDirty: () => this._dirty,
      confirmMessage: this.unsavedMessageValue,
    });

    if (this.flashValue) this._toast(this.flashValue);
  }

  // ==================== Brouillon : commit / abandon (lib/document_draft) ====================

  async commit() {
    if (hasPlaceholderSegment(this._composition)) {
      this._toast(PLACEHOLDER_BLOCKS_SAVE);
      return;
    }
    if (this.hasBtnSaveTarget) this.btnSaveTarget.disabled = true;
    if (await this._draft.commit()) return;

    this._refreshSaveButton();
    this._setSaveState("error");
  }

  // Quitter (documents/_draft_actions, à côté du commit).
  onQuit() {
    this._draft.quit();
  }

  // Télécharger (documents/_draft_actions) : l'url de l'export et le message d'échec sont les
  // params du bouton.
  async download({ currentTarget, params }) {
    currentTarget.disabled = true;
    await this._draft.download(params.url, params.failure);
    currentTarget.disabled = false;
  }

  disconnect() {
    this._autosave?.destroy();
    this._draft?.destroy();
    this.element.removeEventListener("toolbox-item:selected", this._boundOnToolboxItem);
    this.element.removeEventListener("editor-zoom:change", this._boundOnZoom);
    window.removeEventListener("overlay:snap-toggle", this._onSnapToggle);
    window.removeEventListener("overlay:add-anim", this._onAddAnim);
    window.removeEventListener("overlay:remove-anim", this._onRemoveAnim);
    window.removeEventListener("overlay:set-anim-duration", this._onAnimDuration);
    window.removeEventListener("overlay:set-anim-side", this._onAnimSide);
    window.removeEventListener("overlay:set-anim-option", this._onAnimOption);
    this._playback?.destroy();
    window.removeEventListener("background:set-color", this._onSegmentColor);
    window.removeEventListener("background:set-motion", this._onSegmentMotion);
    window.removeEventListener("background:set-motion-amplitude", this._onSegmentMotionAmplitude);
    this._preview.destroy();
    document.removeEventListener("mouseover", this._mouseOverHandler);
    document.removeEventListener("pointerdown", this._pointerDownHandler);
    this._picker?.dispose();
  }

  // ==================== State ====================

  async undo() {
    // En recadrage, ⌘Z annule le recadrage — rien d'autre (il n'est pas encore enregistré)
    const crop = this._fabric?._fabricEditor?.crop;
    if (crop?.active) return crop.cancel();
    if (await this._undo.undo()) {
      this._refreshAfterRestore();
      this._toast("Annulé");
    }
  }

  async redo() {
    if (await this._undo.redo()) {
      this._refreshAfterRestore();
      this._toast("Rétabli");
    }
  }

  /** Called after every mutation. Resolves snaps, syncs inputs, re-renders, and snapshots. */
  _afterMutation() {
    resolveSnaps(this._composition);
    ensureDurationCoversOverlays(this._composition);
    this._dirty = true;
    this._undo.push();
    this._refreshUI();
    this._autosave?.schedule();
  }

  // La pastille (compositions/_save_state) porte ses libellés et son mouvement : l'état suffit.
  _setSaveState(state) {
    if (this.hasSaveStateTarget) this.saveStateTarget.dataset.state = state;
  }

  /** Le format décidé (select du header) : retaille en place — cadre, canvas fabric,
   *  preview — et laisse l'autosave porter les nouvelles dimensions. */
  onDocumentResize({ detail: { width, height } = {} }) {
    if (!width || !height || !this._composition) return;
    this._composition.width = width;
    this._composition.height = height;
    // Le cadre (et l'iframe calée dessus) suit la vue fabric
    this._fabric?.editor?.resizeArtboard?.(width, height);
    this._afterMutation();
  }

  // Le pendant vidéo du « + nouvelle page » de l'éditeur images : une séquence vierge
  // (aplat) de 5 s en fin de timeline. Une vidéo ne naît jamais vide : elle porte déjà un
  // plan à fournir (Composition.blank_video_source_data).
  addBlankSegment() {
    const segments = (this._composition.segments || []).filter((seg) => !seg._trailing);
    segments.push({ id: segmentId(), type: "color", backgroundColor: "#000000", duration: 5 });
    this._composition.segments = segments;
    this._afterMutation();
    this._toast("Séquence ajoutée");
  }

  // Le bouton « Transitions » de la timeline : la même transition sur toutes les jointures,
  // en écrasant les précédentes (lib/composition_v3#setAllTransitions). Le choix par jointure
  // viendra plus tard, sur le même format.
  setTransitions({ params: { kind } = {} }) {
    if (!TRANSITION_KINDS.includes(kind)) return;
    setAllTransitions(this._composition, kind);
    this._afterMutation();
    this._toast(t(kind === "none" ? "composition_editor.transitions.cleared" : "composition_editor.transitions.done"), true);
  }

  // L'import serveur (Documents::CompositionImportsController, StickerImportsController)
  // diffuse le document greffé :
  // on l'adopte tel quel — la base est déjà à jour ; le refresh recale les snaps, recouvre
  // les overlays (segment _trailing) et laisse l'autosave persister ces dérivés.
  async adoptImported({ detail }) {
    if (!detail?.document || !this._autosave) return;

    this._composition = ensureDimensions(ensureSegmentIds(detail.document));
    this._sel = null;
    // Les habillages importés vivent dans _fabricLayers : le canvas se reconstruit, comme
    // après un undo.
    await this._fabric?.restoreOverlays();
    this._undo.push();
    this._refreshAfterRestore();
    this._toast("Composition importée");
  }

  // Un plan IA réglé côté serveur (Composition#broadcast_shot_settled) : la séquence qui
  // référence ce fichier reçoit son url — ou son erreur, et garde sa photo. Pas une édition :
  // ni undo, ni dirty — le document en base a déjà l'issue.
  adoptShotOutcome({ detail }) {
    const { content_medium_id: id, url, error } = detail || {};
    let filled = false;

    for (const seg of this._composition.segments || []) {
      if (seg.src?.content_medium_id !== id || !isMediumMissing(seg)) continue;

      seg.src = { ...seg.src, url: url ?? null, error: error ?? null };
      if (url && seg.type === "video" && seg.sourceEnd == null) {
        Object.assign(seg, { sourceStart: 0, sourceEnd: seg.duration, sourceDuration: seg.duration, speed: 1 });
      }
      filled = true;
    }
    if (!filled) return;

    this._tracks?.renderAll();
    this._sendToPreview();
    this._updateJsonEditor();
    if (error) this._toast(t("composition_editor.ai_generation_failed"));
  }

  /** Called after undo/redo restore. Same as _afterMutation but without pushing to undo stack. */
  _refreshAfterRestore() {
    resolveSnaps(this._composition);
    ensureDurationCoversOverlays(this._composition);
    this._refreshUI();
    this._autosave?.schedule();
  }

  _refreshUI() {
    this._updateJsonEditor();
    this._refreshSaveButton();
    this._tracks?.renderAll();
    this._sendToPreview();
    this._fabric?.refreshPage();
    this.publishPlacement();
    this._announcePage();
  }

  // La séquence sélectionnée est la page de l'éditeur pour la toolbox (`page:changed`, comme
  // l'éditeur images) : ce qui n'a de sens que sur une séquence répétée ne se montre que là.
  _announcePage() {
    const seg = this._sel?.kind === "segment" ? this._composition.segments?.[this._sel.index] : null;
    window.dispatchEvent(new CustomEvent("page:changed", { detail: { id: seg?.id || null, repeat: !!seg?.repeat } }));
  }

  get _fabric() {
    return this.hasCompositionEditorFabricOutlet ? this.compositionEditorFabricOutlet : null;
  }

  get _tracks() {
    return this.hasCompositionEditorTracksOutlet ? this.compositionEditorTracksOutlet : null;
  }

  _sendToPreview() {
    this._preview.sendComposition(this._previewPayload());
  }

  _sendToPreviewAndWait() {
    return this._preview.sendCompositionAndWait(this._previewPayload());
  }

  // Le payload de preview est un RENDU d'affichage : les habillages liés partent interpolés
  // pour ce lecteur (copies — le document garde ses tokens, comme le canvas fabric).
  _previewPayload() {
    const payload = { ...this._composition, _mode: this._playback?.rendersBlocks ? "playing" : "editing" };
    if (this._fabric) payload.overlays = this._fabric.interpolatedOverlays(payload.overlays);
    return payload;
  }

  // ==================== Selection ====================

  _select(kind, index) {
    const previous = this._sel;
    this._sel = kind ? { kind, index } : null;
    this._tracks?.renderAll();

    // Une séquence sélectionnée, c'est la page de la vidéo : sa barre de fond (FabricControls),
    // et la tête de lecture sur elle — le cadre montre le fond que la barre règle. L'overlay a
    // déjà son propre flux (fabric _onSelect/_onDeselect).
    if (kind === "segment" && (previous?.kind !== "segment" || previous.index !== index)) {
      this._seekIntoSegment(index);
      this._fabric?.showSegmentPage(index);
    } else if (kind !== "segment" && previous?.kind === "segment") {
      this._fabric?.deselectPage();
    }
    this._fabric?.refreshPage();
    this._announcePage();
    this.publishPlacement();
  }

  /**
   * La tête de lecture sur la séquence, si elle n'y est pas déjà : sur sa première frame où
   * elle est seule à l'écran. Sa frame de début peut encore montrer la précédente — le fondu
   * qui les enchaîne en chevauche une demi-durée (creatorstudio computeSegmentTimeline), et
   * l'arrondi en frames peut y tomber.
   */
  _seekIntoSegment(index) {
    const segments = this._composition.segments || [];
    const seg = segments[index];
    if (!seg) return;

    const start = segmentOutputStart(this._composition, index);
    const end = start + segmentOutputDuration(seg);
    if (this.playTime >= start && this.playTime < end) return;

    const fade = segments[index - 1]?.transition;
    const overlap = fade && !fade.color ? (fade.duration || 0) / 2 : 0;
    const frame = 1 / (this._composition.fps || 30);
    this.goTo(Math.min(start + overlap + frame, (start + end) / 2));
  }

  // Le color picker de la page Fond : peint le fond de la séquence sélectionnée — elle
  // devient un aplat, son média (s'il en avait un) s'en va ; le plan à fournir et le
  // bouche-trou deviennent une vraie séquence. backgroundColor porte une valeur CSS (hex ou
  // linear-gradient) : toute la chaîne — player Remotion (Transition), preview serveur, bloc
  // timeline — la pose en `background`, jamais en backgroundColor strict.
  // La valeur du color picker (hex, rgba, linear-gradient) est du CSS : le backgroundColor
  // de la séquence la porte telle quelle.
  _handleSegmentColor({ value } = {}) {
    const sel = this._sel;
    if (sel?.kind !== "segment") return;
    const seg = this._composition.segments?.[sel.index];
    if (!seg) return;

    const background = value;
    if (!background || background === "transparent") return;

    seg.type = "color";
    seg.backgroundColor = background;
    seg.duration = segmentOutputDuration(seg) || 5;
    for (const key of ["src", "sourceStart", "sourceEnd", "sourceDuration", "placeholder", "_trailing"]) delete seg[key];
    this._afterMutation();
  }

  /** La séquence sélectionnée, si son fond est une image : la seule qui bouge (page Mouvement). */
  _selectedImageSegment() {
    const sel = this._sel;
    if (sel?.kind !== "segment") return null;
    const seg = this._composition.segments?.[sel.index];
    return seg?.type === "image" ? seg : null;
  }

  /** Page Mouvement : la sorte de mouvement de l'image de fond (lib/segment_motion), à
   *  l'amplitude déjà réglée. */
  _handleSegmentMotion({ value } = {}) {
    const seg = this._selectedImageSegment();
    if (!seg || !MOTION_KINDS.includes(value)) return;
    this._setSegmentMotion(seg, value, motionOf(seg.animation).amplitude);
  }

  /** Page Mouvement : l'amplitude du mouvement en cours. Sans mouvement, rien à régler. */
  _handleSegmentMotionAmplitude({ value } = {}) {
    const seg = this._selectedImageSegment();
    const { kind } = motionOf(seg?.animation);
    if (!seg || kind === "none" || !Number.isFinite(value)) return;
    this._setSegmentMotion(seg, kind, value);
  }

  _setSegmentMotion(seg, kind, amplitude) {
    const animation = motionAnimation(kind, amplitude);
    if (animation) seg.animation = animation;
    else delete seg.animation;
    this._afterMutation();
  }

  restoreSelection() {
    if (!this._selBeforePlay) return;
    this._select(this._selBeforePlay.kind, this._selBeforePlay.index);
    if (this._selBeforePlay.kind === "overlay") {
      this._fabric?.selectOverlay(this._selBeforePlay.index);
    }
    this._selBeforePlay = null;
  }

  // Le `disabled` seul laisserait l'utilisateur sans explication : un bouton inerte n'émet pas de
  // survol, donc l'infobulle est portée par l'enveloppe, et `disabled:pointer-events-none` lui
  // laisse passer la souris.
  _refreshSaveButton() {
    if (!this.hasBtnSaveTarget) return;

    const blocked = hasPlaceholderSegment(this._composition);
    this.btnSaveTarget.disabled = blocked;

    if (!this.hasBtnSaveWrapperTarget) return;

    if (blocked) this.btnSaveWrapperTarget.dataset.tip = PLACEHOLDER_BLOCKS_SAVE;
    else delete this.btnSaveWrapperTarget.dataset.tip;
  }

  // ==================== Media library ====================

  // Remplir une séquence (plan à fournir, ou remplacement de son média) : elle garde sa place
  // et son id, prend la nature du média choisi — une image reste une image, jamais un
  // segment « video » sur une source image (le plan noir).
  async _replaceSegmentMedia(contentMedium, ctx) {
    const seg = this._composition.segments[ctx.index];
    if (!seg) return;

    const fields = await this._segmentFromMedium(contentMedium, seg.duration || 5);
    Object.assign(seg, fields, { speed: seg.speed || 1 });
    if (fields.type !== "video") delete seg.sourceDuration;
    delete seg.placeholder;
    delete seg._trailing;
    delete seg.backgroundColor;
    // Le cadrage était celui de l'image d'avant
    delete seg.crop;
    // Une carte dynamique lie le fond au sujet (l'exemple reste pour l'aperçu) ; un vrai
    // média délie.
    if (ctx.dynamic) bindSegmentSlot(seg, ctx.dynamic); else unbindSegmentSlot(seg);

    this._afterMutation();
    this._toast(ctx.dynamic ? "Média dynamique posé" : "Média importé");
  }

  // Le segment d'un média de la bibliothèque — la recette commune à l'insertion et au
  // remplissage. Une vidéo prend sa durée réelle, une image la durée par défaut.
  async _segmentFromMedium(contentMedium, fallbackDuration = 5) {
    const isVideo =
      (contentMedium.media_type || contentMedium.serialized_file?.resource_type) === "video";
    const duration = isVideo
      ? contentMedium.duration ||
        (await this._getVideoDuration(contentMedium.url).catch(() => null)) ||
        fallbackDuration
      : fallbackDuration;

    return {
      type: isVideo ? "video" : "image",
      src: { url: contentMedium.url, content_medium_id: Number(contentMedium.id) },
      sourceStart: 0,
      sourceEnd: duration,
      sourceDuration: isVideo ? duration : undefined,
      duration,
      speed: 1,
      muted: this._hasMusic(),
    };
  }

  _getVideoDuration(url) {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.crossOrigin = "anonymous";
      video.onloadedmetadata = () => {
        const d = video.duration;
        video.src = "";
        resolve(d && isFinite(d) ? d : null);
      };
      video.onerror = () => {
        console.warn("[composition-editor] Could not load video metadata:", url);
        reject();
      };
      video.src = url;
    });
  }

  // Le « + » de la timeline : un plan à fournir à cette place — jamais après le bouche-trou,
  // dérivé —, sélectionné : la tête de lecture y va, la barre de son fond se pose sur le
  // cadre, et la toolbox ouvre son fond vidéo.
  _insertPlaceholderSegment(index) {
    const segments = this._composition.segments || [];
    const trailing = segments.findIndex((seg) => seg._trailing);
    const at = Math.min(index, trailing === -1 ? segments.length : trailing);
    segments.splice(at, 0, {
      id: segmentId(), type: "video", placeholder: true, duration: 5, backgroundColor: "#000000",
    });
    this._composition.segments = segments;
    this._afterMutation();
    this.openSegmentBackground(at, "video");
  }

  // Une carte dynamique (stade template) fait une séquence LIÉE au sujet : l'exemple pour
  // l'aperçu, le slot pour l'instanciation — comme le remplacement d'un fond.
  async _insertSegmentFromMedia(contentMedium, ctx) {
    const atIndex = ctx.index;
    const seg = { id: segmentId(), ...(await this._segmentFromMedium(contentMedium)) };
    if (ctx.dynamic) bindSegmentSlot(seg, ctx.dynamic);

    const segs = this._composition.segments || [];
    segs.splice(atIndex, 0, seg);
    this._composition.segments = segs;
    this._afterMutation();
    this._toast(ctx.dynamic ? "Média dynamique posé" : "Média ajouté");
  }

  // ==================== JSON drawer ====================

  toggleJsonDrawer() {
    this._jsonDrawerOpen = !this._jsonDrawerOpen;
    this.jsonDrawerTarget.classList.toggle("hidden", !this._jsonDrawerOpen);
    if (this._jsonDrawerOpen) this._updateJsonEditor();
  }

  _updateJsonEditor() {
    if (!this.hasEditorTarget) return;
    this.editorTarget.value = JSON.stringify(this._composition, null, 2);
    const td = totalOutputDuration(this._composition);
    this.infoTarget.textContent = `${this._composition.segments?.length || 0} segments, ${td.toFixed(1)}s @ ${this._composition.fps || 30}fps`;
  }

  onJsonInput() {
    try {
      const c = JSON.parse(this.editorTarget.value);
      this._composition = ensureDimensions(ensureSegmentIds(c));
      this.errorTarget.classList.add("hidden");
      this._tracks?.renderAll();
      this._sendToPreview();
    } catch (e) {
      this.errorTarget.textContent = e.message;
      this.errorTarget.classList.remove("hidden");
    }
  }

  // ==================== Playback ====================

  togglePlay() {
    this._playback.toggle();
  }

  /** Une lecture de plage (cf. lib/editor_playback) : l'aperçu d'une animation. */
  playRange(from, to, opts) {
    this._playback.playRange(from, to, opts);
  }

  goToStart() {
    this.goTo(0);
  }
  goToEnd() {
    this.goTo(totalOutputDuration(this._composition));
  }

  // ==================== Zoom ====================

  /** Le zoom passe par la vue fabric (plan de travail) : le cadre, l'iframe calée dessus
   *  et les calques suivent — net à tout zoom, sans scale CSS. */
  _applyZoom(zoom) {
    if (typeof zoom !== "number") return;
    this._fabric?.editor?.setUserZoom(zoom);
  }

  // ==================== Toast ====================

  _toast(msg, withUndo = false) {
    const el = this._tpl.clone("toastTpl");
    el.querySelector('[data-role="message"]').textContent = msg;
    if (withUndo) {
      el.querySelector('[data-role="undo"]').classList.remove("hidden");
      el.querySelector('[data-role="spacer"]').classList.add("hidden");
    }
    this.toastsTarget.appendChild(el);
    animateIn(el, { y: 14, duration: 0.36 });
    let gone = false;
    const dismiss = () => {
      if (gone) return;
      gone = true;
      animateOut(el, { onComplete: () => el.remove() });
    };
    setTimeout(dismiss, withUndo ? 5000 : 2200);
  }

  onToastUndo() {
    this.undo();
  }

  // ==================== Toolbox item handling ====================

  /** Un item cliqué — ou un upload arrivé, qui porte sa cible figée au dépôt. */
  _onToolboxItemSelected(e) {
    const detail = e.detail;
    if (!detail?.type) return;
    if (detail.type !== "media") {
      this._fabric?.placeToolboxItem(detail);
      return;
    }

    const placement = "placement" in detail ? detail.placement : this._placements()[mediaKind(detail)];
    this._placeMedia(detail, placement);
  }

  /**
   * Où irait un média de chaque famille. La séquence sélectionnée (la page) le prend pour
   * fond, image ou vidéo. Sinon une image va sur le canvas, et une vidéo, qui n'y a pas sa
   * place, rejoint la timeline en fin de séquence (le drag reste possible sur la piste médias).
   */
  _placements() {
    const segments = this._composition?.segments || [];
    if (this._sel?.kind === "segment") {
      const segment = segments[this._sel.index];
      const placement = segment?.id ? { action: "segment-background", segment: segment.id } : null;
      return { image: placement, video: placement };
    }
    return { image: this._fabric?.canvasPlacement() ?? null, video: { action: "append-segment" } };
  }

  /** Un média posé sur sa cible ; une séquence visée disparue ne se rabat sur rien. Une carte
   *  dynamique (stade template) se pose même sans exemple : le slot vaut par son binding. */
  _placeMedia(item, placement) {
    if (!placement || (!item.url && !item.dynamic)) return;

    const segments = this._composition.segments || [];
    const cm = { url: item.url, id: item.id, media_type: item.media_type };
    switch (placement.action) {
      case "segment-background": {
        const index = segments.findIndex((segment) => segment.id === placement.segment);
        if (index !== -1) this._replaceSegmentMedia(cm, { index, dynamic: item.dynamic });
        return;
      }
      case "append-segment":
        this._insertSegmentFromMedia(cm, { index: segments.length, dynamic: item.dynamic });
        return;
      default:
        this._fabric?.placeToolboxItem(item, placement);
    }
  }

  // ==================== Les raccourcis ====================
  // Ceux de l'orchestrateur — le sous-contrôleur fabric les enregistre dans le gestionnaire
  // de la lib dès que l'éditeur existe (la table du canvas y est déjà : supprimer, dupliquer,
  // grouper, décaler…). Une entrée rend false quand elle n'est pas concernée.
  shortcutEntries() {
    const segment = () => (this._sel?.kind === "segment" ? this._sel.index : null);
    return [
      { id: "undo", keys: ["mod+z"], group: "edit", run: () => this.undo() },
      { id: "redo", keys: ["mod+shift+z", "mod+y"], group: "edit", run: () => this.redo() },
      { id: "cut", keys: ["c"], group: "timeline", run: () => this._tracks?.cutAtPlayhead() },
      { id: "play", keys: ["Space"], group: "timeline", run: () => this.togglePlay() },
      { id: "go-start", keys: ["Home"], group: "timeline", run: () => this.goToStart() },
      { id: "go-end", keys: ["End"], group: "timeline", run: () => this.goToEnd() },
      // Un geste du canvas en cours : Échap l'annule (la lib), la sélection de la timeline reste
      { id: "timeline-escape", keys: ["Escape"], group: "timeline",
        run: () => !this._fabric?.gestureActive &&
          (this._select(null), this._tracks?.hideSpeedPop(), true) },
      { id: "delete-segment", keys: ["Delete", "Backspace"], group: "timeline",
        run: () => segment() !== null && (this._tracks?.deleteSegment(segment()), true) },
    ];
  }

  // ==================== La musique ====================
  // Posée par la page Audio de la toolbox (`audio:set`) : le document garde les réglages en
  // place (découpe, fondus, volume) et prend le morceau ; les segments vidéo se taisent.
  setAudio({ detail: { track } }) {
    if (!track?.url) return;
    const previous = this._composition.audio || {};
    this._composition.audio = {
      src: track.url,
      name: track.title,
      track: trackReference(track),
      ...audioSettings(previous),
    };
    this._syncSegmentAudio();
    this._afterMutation();
    this.seekTo(Math.round(this.playTime * (this._composition.fps || 30)));
  }

  // Un template : la musique est une liste de candidats (`audio.candidates`), un morceau
  // tiré par vidéo générée à l'expansion (CompositionDocument::Music). Mêmes réglages qu'un
  // morceau posé ; plus de candidat, plus de musique.
  toggleAudioCandidate({ detail: { track } }) {
    if (!track?.id) return;
    const candidates = audioCandidates(this._composition.audio);
    const present = candidates.some((candidate) => candidate.id === track.id);
    this._setAudioCandidates(present ? candidates.filter((candidate) => candidate.id !== track.id)
                                     : [...candidates, trackReference(track)]);
  }

  removeAudioCandidate({ detail: { id } }) {
    this._setAudioCandidates(audioCandidates(this._composition.audio).filter((candidate) => candidate.id !== id));
  }

  _setAudioCandidates(candidates) {
    const previous = this._composition.audio || {};
    this._composition.audio = candidates.length ? { candidates, ...audioSettings(previous) } : null;
    this._afterMutation();
  }

  removeAudio() {
    if (!this._composition.audio) return;
    this._composition.audio = null;
    if (this._sel?.kind === "audio") this._sel = null;
    this._syncSegmentAudio();
    this._afterMutation();
  }

  // ==================== Segment audio ====================

  _hasMusic() {
    return !!this._composition.audio?.src;
  }

  // HACK: Pour blot et les vidéos visite temporairement on ajoute la possibilité de garder l'audio de la vidéo
  // importé dans la visite. La règle est si il y a de la musique sur le segment on mute,
  // s'il y en a pas on garde le son de la vidéo uploadé
  _syncSegmentAudio() {
    const hasMusic = this._hasMusic();
    (this._composition.segments || []).forEach((seg) => {
      if (seg.type === "video") seg.muted = hasMusic;
    });
  }

  // ==================== Overlay snap / animation ====================

  _selectedOverlay() {
    const sel = this._sel;
    if (sel?.kind !== "overlay") return null;
    return this._composition.overlays?.[sel.index] || null;
  }

  _handleSnapToggle({ anchor, enabled }) {
    const overlay = this._selectedOverlay();
    if (!overlay) return;

    if (!overlay.snap) overlay.snap = {};

    if (enabled) {
      const t = anchor === "start" ? (overlay.start || 0) : (overlay.end || 0);
      const ref = this._snapRefForTime(t, anchor);
      if (ref) {
        if (anchor === "start") overlay.snap.start = ref;
        else overlay.snap.end = ref;
      }
    } else {
      if (anchor === "start") delete overlay.snap.start;
      else delete overlay.snap.end;
      if (!overlay.snap.start && !overlay.snap.end) delete overlay.snap;
    }

    this._afterMutation();
  }

  _handleAddAnim({ anim }) {
    const overlay = this._selectedOverlay();
    if (!overlay || !anim) return;
    if (!overlay.animations) overlay.animations = [];

    overlay.animations.push(anim);
    recalcAnimations(overlay);

    this._afterMutation();
    this._previewAnimation(overlay, anim);
  }

  _handleRemoveAnim({ index }) {
    const overlay = this._selectedOverlay();
    if (!overlay?.animations?.[index]) return;

    overlay.animations.splice(index, 1);
    if (!overlay.animations.length) delete overlay.animations;

    this._afterMutation();
  }

  _handleAnimDuration({ index, duration }) {
    const anim = this._selectedOverlay()?.animations?.[index];
    if (!anim || !(duration > 0)) return;

    anim.duration = duration;

    this._afterMutation();
    this._previewAnimation(this._selectedOverlay(), anim);
  }

  /** Entrée | Sortie : l'animation devient sa paire (l'entrée jouée à l'envers), ancrée au
   *  bout correspondant de la présence du bloc. La durée reste celle qu'on avait réglée. */
  _handleAnimSide({ index, side }) {
    const overlay = this._selectedOverlay();
    const anim = overlay?.animations?.[index];
    if (!anim) return;

    const current = preset(anim.preset);
    const wanted = side === "out" ? "exit" : "entrance";
    const name = current?.group === wanted ? anim.preset : current?.pair;
    const def = name && preset(name);
    if (!def) return;

    Object.assign(anim, { preset: name, primitive: def.primitive, from: def.from, to: def.to, easing: def.easing });
    if (side === "out") {
      anim.fromEnd = true;
      anim._fromEndDelay = anim._fromEndDelay || 0;
      delete anim.delay;
    } else {
      delete anim.fromEnd;
      delete anim._fromEndDelay;
      anim.delay = anim.delay || 0;
    }
    recalcAnimations(overlay);

    this._afterMutation();
    this._previewAnimation(overlay, anim);
  }

  /** Un réglage propre au preset (la couleur d'un balayage) : seules les clés que son
   *  catalogue déclare (`options`) s'écrivent dans l'animation. */
  _handleAnimOption({ index, key, value }) {
    const overlay = this._selectedOverlay();
    const anim = overlay?.animations?.[index];
    if (!anim || !preset(anim.preset)?.options?.includes(key)) return;

    anim[key] = value;

    this._afterMutation();
    this._previewAnimation(overlay, anim);
  }

  /** L'aperçu d'une animation qu'on vient de poser ou de régler : sa plage seule, sans le
   *  son — une entrée au début de la présence du bloc (après son délai), une sortie à sa fin. */
  _previewAnimation(overlay, anim) {
    if (!overlay || !anim) return;
    const duration = Number(anim.duration) || 0.5;
    const from = anim.fromEnd
      ? overlay.end - duration - (anim._fromEndDelay || 0)
      : overlay.start + (anim.delay || 0);
    const start = Math.max(0, from);
    this.playRange(start, start + duration, { muted: true });
  }

  _snapRefForTime(t, anchor = "start") {
    const segs = this._composition.segments || [];
    let acc = 0;
    for (const seg of segs) {
      const dur = segmentOutputDuration(seg);
      if (t <= acc + dur) {
        if (anchor === "end") {
          const offset = Math.round((t - (acc + dur)) * 100) / 100;
          return { segId: seg.id, edge: "end", offset };
        }
        return { segId: seg.id, offset: Math.round((t - acc) * 100) / 100 };
      }
      acc += dur;
    }
    const last = segs[segs.length - 1];
    if (last) return { segId: last.id, edge: "end", offset: 0 };
    return null;
  }
}
